import React, { useState, useEffect } from 'react';
import { useData } from '../../hooks/useData';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import DashboardModule from '../dashboard/DashboardModule';
import StudiesModule from '../studies/StudiesModule';
import FeasibilityModule from '../feasibility/FeasibilityModule';
import ProjectTrackingModule from '../project/ProjectTrackingModule';
import RiskModule from '../risk/RiskModule';
import KnowledgeLibraryModule from '../knowledge/KnowledgeLibraryModule';
import SearchModule from '../knowledge/SearchModule';
import ReportsModule from '../reports/ReportsModule';
import AuditModule from '../governance/AuditModule';
import AdminModule from '../governance/AdminModule';
import AuthWrapper from '../../auth/AuthWrapper';
import BioequivalenceCrossover from '../bioequivalence/BioequivalenceCrossover';
import BioequivalenceParallel from '../bioequivalence/BioequivalenceParallel';
import BioequivalencePartialReplicate from '../bioequivalence/BioequivalencePartialReplicate';
import BioequivalenceFullReplicate from '../bioequivalence/BioequivalenceFullReplicate';
import ProtocolReviewModule from '../studyProtocolReview/ProtocolReviewModule';
import CSRListingReviewModule from '../studyProtocolReview/CSRListingReviewModule';
import CSRReviewModule from '../studyProtocolReview/CSRReviewModule';
import AuditTrail from '../governance/AuditTrail';
import LogHistory from '../governance/LogHistory';
import FailedLoginHistory from '../governance/FailedLoginHistory';
import UserProfileSettings from '../Settings/UserProfileSettings';
import ChangePassword from '../Settings/ChangePassword';
import TwofactorAuth from '../Settings/TwofactorAuth';

const MODULES = {
  dashboard: {
    id: 'dashboard',
    name: 'Dashboard',
    icon: 'ti-layout-dashboard',
    group: 'workspace',
    crumbs: ['Workspace', 'Dashboard'],
    component: DashboardModule
  },
  studies: {
    id: 'studies',
    name: 'Studies & Documents',
    icon: 'ti-folder',
    group: 'workspace',
    crumbs: ['Workspace', 'Studies & Documents'],
    component: StudiesModule
  },
  feasibility: {
    id: 'feasibility',
    name: 'Feasibility',
    icon: 'ti-checklist',
    group: 'workspace',
    crumbs: ['Workspace', 'Feasibility'],
    component: FeasibilityModule
  },
  project: {
    id: 'project',
    name: 'Project tracking',
    icon: 'ti-route',
    group: 'workspace',
    crumbs: ['Workspace', 'Project tracking'],
    component: ProjectTrackingModule
  },
  protocolReview: {
    id: 'protocolReview',
    name: 'Protocol Review',
    icon: 'ti-file-text',
    group: 'studyProtocolReview',
    crumbs: ['Study Document Review', 'Protocol Review'],
    component: ProtocolReviewModule
  },
  csrReview: {
    id: 'csrReview',
    name: 'CSR Review',
    icon: 'ti-file-description',
    group: 'studyProtocolReview',
    crumbs: ['Study Document Review', 'CSR Review'],
    component: CSRReviewModule
  },
  csrListingReview: {
    id: 'csrListingReview',
    name: 'CSR Listing Review',
    icon: 'ti-list-check',
    group: 'studyProtocolReview',
    crumbs: ['Study Document Review', 'CSR Listing Review'],
    component: CSRListingReviewModule
  },
  bioequivalence_crossover: {
    id: 'bioequivalence_crossover',
    name: 'BE — 2×2 Crossover (TOST)',
    icon: 'ti-arrows-left-right',
    group: 'bioequivalence',
    crumbs: ['Bioequivalence', 'BE — 2×2 Crossover (TOST)'],
    component: BioequivalenceCrossover
  },
  bioequivalence_parallel: {
    id: 'bioequivalence_parallel',
    name: 'BE — Parallel Design (TOST)',
    icon: 'ti-layout-sidebar',
    group: 'bioequivalence',
    crumbs: ['Bioequivalence', 'BE — Parallel Design (TOST)'],
    component: BioequivalenceParallel
  },
  bioequivalence_partial: {
    id: 'bioequivalence_partial',
    name: 'BE — Partial Replicate (TRR/RTR/RRT)',
    icon: 'ti-columns-3',
    group: 'bioequivalence',
    crumbs: ['Bioequivalence', 'BE — Partial Replicate (TRR/RTR/RRT)'],
    component: BioequivalencePartialReplicate
  },
  bioequivalence_full: {
    id: 'bioequivalence_full',
    name: 'BE — Fully Replicate (TRTR/RTRT)',
    icon: 'ti-grid-dots',
    group: 'bioequivalence',
    crumbs: ['Bioequivalence', 'BE — Fully Replicate (TRTR/RTRT)'],
    component: BioequivalenceFullReplicate
  },
  risk: {
    id: 'risk',
    name: 'Risk analytics',
    icon: 'ti-shield',
    group: 'workspace',
    crumbs: ['Workspace', 'Risk analytics'],
    component: RiskModule
  },
  knowledge: {
    id: 'knowledge',
    name: 'Knowledge library',
    icon: 'ti-books',
    group: 'intelligence',
    crumbs: ['Intelligence', 'Knowledge library'],
    component: KnowledgeLibraryModule
  },
  search: {
    id: 'search',
    name: 'Knowledge search',
    icon: 'ti-message-chatbot',
    group: 'intelligence',
    crumbs: ['Intelligence', 'Knowledge search'],
    component: SearchModule
  },
  reports: {
    id: 'reports',
    name: 'Reports',
    icon: 'ti-file-text',
    group: 'intelligence',
    crumbs: ['Intelligence', 'Reports'],
    component: ReportsModule
  },
  audit: {
    id: 'audit',
    name: 'Audit trail',
    icon: 'ti-history',
    group: 'governance',
    crumbs: ['Governance', 'Audit trail'],
    component: AuditModule
  },
  admin: {
    id: 'admin',
    name: 'Admin',
    icon: 'ti-settings',
    group: 'governance',
    crumbs: ['Governance', 'Admin'],
    component: AdminModule
  },
  audit: {
    name: 'Audit trail',
    icon: 'ti-history',
    group: 'governance',
    crumbs: ['Governance', 'Audit trail'],
    component: AuditTrail,
  },
  logHistory: {
    name: 'Log History',
    icon: 'ti-history',
    group: 'governance',
    crumbs: ['Governance', 'Log History'],
    component: LogHistory,
  },
  failedLogHistory: {
    name: 'Failed Log History',
    icon: 'ti-history',
    group: 'governance',
    crumbs: ['Governance', 'Failed Log History'],
    component: FailedLoginHistory,
  },
  profile: {
    name: 'Profle',
    icon: 'ti-user-check',
    group: 'profile',
    crumbs: ['Profile', 'Profle'],
    component: UserProfileSettings,
  },
  changePassword: {
    name: 'Change Password',
    icon: 'ti-lock',
    group: 'profile',
    crumbs: ['Profile', 'Change Password'],
    component: ChangePassword,
  },
  twoFA: {
    name: 'Two factor Auth',
    icon: 'ti-shield',
    group: 'profile',
    crumbs: ['Profile', 'Two factor Auth'],
    component: TwofactorAuth,
  },
};

const GROUPS = [
  { id: 'workspace', label: 'Workspace' },
  { id: 'studyProtocolReview', label: 'Study Document Review' },
  { id: 'intelligence', label: 'Intelligence engine' },
  { id: 'governance', label: 'Governance' },
  { id: 'profile', label: 'Profile' },
];

function AppShell() {
  const { currentUserData } = useData();

  const [active, setActive] = useState('dashboard');
  const [topSearch, setTopSearch] = useState('');

  const ActiveComponent = MODULES[active]?.component || DashboardModule;
  const crumbs = MODULES[active]?.crumbs || ['Dashboard'];

  return (
    <div className="app">
      <Sidebar
        active={active}
        setActive={setActive}
        groups={GROUPS}
        modules={MODULES}
        counts={{}}
        currentUserData={currentUserData}
      />

      <main className="main">
        <TopBar
          crumbs={crumbs}
          topSearch={topSearch}
          setTopSearch={setTopSearch}
          currentUserData={currentUserData}
        />

        <div className="page">
          {ActiveComponent && <ActiveComponent onNavigate={setActive} />}
        </div>
      </main>
      <style>{`
        @keyframes spin { 
          from { transform: rotate(0deg); } 
          to { transform: rotate(360deg); } 
        }
        .ti-loader-2 {
          display: inline-block;
        }
      `}</style>
    </div>
  );
}

export default AppShell;